"use client";

import { m as motion, useReducedMotion } from "framer-motion";
import { useRef, useState, type ReactNode } from "react";

import { RevealOnScroll } from "@/components/primitives/RevealOnScroll";
import { useCanHover } from "@/hooks/useCanHover";
import { Link } from "@/i18n/navigation";

type Props = {
  tag: string;
  name: string;
  description: string;
  visitLabel?: string;
  href: string;
  visual: ReactNode;
  delay?: number;
};

export function ProductCard({
  tag,
  name,
  description,
  visitLabel,
  href,
  visual,
  delay = 0,
}: Props) {
  const prefersReduced = useReducedMotion();
  const ref = useRef<HTMLAnchorElement | null>(null);
  const [hovered, setHovered] = useState(false);
  const [rotate, setRotate] = useState({ x: 0, y: 0 });
  const [glow, setGlow] = useState({ x: 50, y: 50 });
  const canTilt = useCanHover() === true;

  function handleMove(e: React.PointerEvent<HTMLAnchorElement>) {
    if (prefersReduced || !canTilt) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    setRotate({ x: (py - 0.5) * -8, y: (px - 0.5) * 8 });
    setGlow({ x: px * 100, y: py * 100 });
  }

  function handleLeave() {
    setHovered(false);
    setRotate({ x: 0, y: 0 });
  }

  return (
    <RevealOnScroll delay={delay}>
      <Link
        ref={ref}
        href={href}
        aria-label={name}
        onPointerEnter={() => setHovered(true)}
        onPointerMove={handleMove}
        onPointerLeave={handleLeave}
        className="group relative block cursor-pointer"
      >
        <motion.div
          className="relative min-h-[360px] overflow-hidden rounded-2xl border bg-bg-elevated p-6 md:min-h-[420px] md:p-9"
          style={{
            borderColor: hovered
              ? "rgba(59,126,255,0.4)"
              : "var(--color-border-hairline)",
            boxShadow: hovered
              ? "0 18px 60px rgba(30,63,232,0.25), 0 0 0 1px rgba(59,126,255,0.3)"
              : "none",
            transformStyle: "preserve-3d",
            transformPerspective: 1200,
            transition: "border-color .4s ease, box-shadow .4s ease",
          }}
          animate={{ rotateX: rotate.x, rotateY: rotate.y }}
          transition={{ type: "spring", stiffness: 180, damping: 18 }}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-[inherit] transition-opacity duration-500"
            style={{
              background: `radial-gradient(600px circle at ${glow.x}% ${glow.y}%, rgba(59,126,255,0.18), transparent 40%)`,
              opacity: hovered ? 1 : 0,
            }}
          />

          <span
            aria-hidden
            className="pointer-events-none absolute right-5 top-5 inline-flex h-9 w-9 items-center justify-center rounded-full border text-[14px] text-brand-azure"
            style={{
              borderColor: "rgba(59,126,255,0.4)",
              background: "rgba(59,126,255,0.08)",
              opacity: hovered ? 1 : 0,
              transform: hovered
                ? "translate3d(0,0,40px)"
                : "translate3d(0,-4px,0)",
              transition:
                "opacity .3s cubic-bezier(0.22, 1, 0.36, 1), transform .3s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            ↗
          </span>

          <span
            className="mb-7 inline-block rounded-full border px-[10px] py-[5px] font-mono text-[11px] uppercase text-brand-azure"
            style={{
              background: "var(--accent-soft)",
              borderColor: "rgba(59,126,255,0.25)",
              letterSpacing: "0.18em",
            }}
          >
            {tag}
          </span>

          <h3
            className="mb-3 font-display font-bold"
            style={{
              fontSize: "clamp(28px, 5vw, 40px)",
              letterSpacing: "-0.02em",
              transform: "translateZ(30px)",
            }}
          >
            {name}
          </h3>

          <p
            className="mb-7 max-w-[420px] text-base leading-relaxed text-fg2 z-50"
            style={{ transform: "translateZ(20px)" }}
          >
            {description}
          </p>

          {visitLabel ? (
            <span
              className="inline-flex items-center gap-2 font-display text-sm font-semibold text-brand-azure"
              style={{ transform: "translateZ(40px)" }}
            >
              {visitLabel}
              <span
                aria-hidden
                className="transition-transform duration-300 group-hover:translate-x-[6px]"
              >
                →
              </span>
            </span>
          ) : null}

          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-40 -right-5 origin-bottom-right scale-[0.55] sm:scale-75 md:scale-100"
          >
            <motion.div
              style={{ transformStyle: "preserve-3d" }}
              animate={
                prefersReduced
                  ? undefined
                  : {
                      z: hovered ? 60 : 40,
                      rotate: hovered ? -3 : -6,
                      scale: hovered ? 1.05 : 1,
                    }
              }
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              {visual}
            </motion.div>
          </div>
        </motion.div>
      </Link>
    </RevealOnScroll>
  );
}
